import ButtonBase from '@/global/components/Button/ButtonBase'
import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import {
    GridColDefPro,
    GridRenderCellParams,
} from '@/global/components/DataGrid/utils/types'
import { cn } from '@/lib/utils'
import { JornadaDiaria, JornadaMensal } from '@/service/api'
import { SN, StatusAprovacao } from '@acme/core'
import { PencilIcon } from 'lucide-react'
import React, { useMemo } from 'react'
import { ExibirDia } from './ExibirDia'
import Ausencias from './ausencias'
import FaltasCell from './faltas'
import { OpcoesDigitacao, useOpcoesDigitacaoHooks } from './opcoes'
import RegistrosCell from './registros-cell'
import {
    SaldoDiarioCell,
    SaldoDiarioMap,
    useSaldoDiarioMap,
} from './saldo-diario'
import { ViewMode } from './toggle-view'

export type EdicaoEspelhoOptions = {
    habilitada: boolean
    onEditarHora: (
        row: JornadaMensal,
        campo: 'horasTrabalhadas' | 'faltas',
    ) => void
    onEditarHorasExtras: (row: JornadaMensal) => void
}

/** Célula com valor + lápis que abre o modal de edição do dia (feature #1522) */
const celulaEditavel =
    (onEditar: (row: JornadaMensal) => void, titulo: string) =>
    ({ value, row }: GridRenderCellParams) => {
        const zerado = !value || value === '00:00'
        return (
            <ButtonBase
                type="button"
                variant="ghost"
                title={titulo}
                className={cn(
                    'h-full gap-1.5 px-2 text-base',
                    zerado
                        ? 'font-normal text-muted-foreground'
                        : 'font-semibold text-foreground',
                )}
                onClick={() => onEditar(row as JornadaMensal)}
            >
                {value || '00:00'}
                <PencilIcon className="size-3 opacity-50" />
            </ButtonBase>
        )
    }

function valueGetterAusencias({ row }: { row: JornadaDiaria | JornadaMensal }) {
    const {
        afastamento,
        compensacao,
        compensacaoManual,
        abono,
        folga,
        feriado,
        faltaCadastrada,
        descontoDsr,
        DSR,
    } = row
    const trocaDiaTrabalho = row.troca
    return {
        afastamento:
            afastamento &&
            afastamento.statusAprovacao !== StatusAprovacao.REPROVADO
                ? afastamento
                : undefined,
        compensacao: compensacao && !feriado ? compensacao : undefined,
        compensacaoManual: compensacaoManual ? compensacaoManual : undefined,
        abono:
            abono && abono.statusAprovacao !== StatusAprovacao.REPROVADO
                ? abono
                : undefined,
        descontoDsr,
        folga,
        trocaDiaTrabalho:
            trocaDiaTrabalho && !folga ? trocaDiaTrabalho : undefined,
        faltaCadastrada: faltaCadastrada ? faltaCadastrada : undefined,
        DSR: DSR ? DSR : undefined,
    }
}

const MemoziedRegistrosCell = React.memo(
    RegistrosCell,
    (prevProps, nextProps) => {
        const { row: prevRow } = prevProps as {
            row: JornadaDiaria | JornadaMensal
        }
        const { row: nextRow } = nextProps as {
            row: JornadaDiaria | JornadaMensal
        }

        return (
            prevRow.registros.map(r => String(r.updatedAt)).join(',') ===
                nextRow.registros.map(r => String(r.updatedAt)).join(',') &&
            prevRow.dia === nextRow.dia &&
            prevRow.isAtivo === nextRow.isAtivo
        )
    },
)

function valueOrNull({ value }: { value: any }) {
    return value === '00:00' ? null : value
}

function mensalColumns(
    /** empresa?.exibirSaldoDiario */
    exibirSaldoDiario: boolean,
    /** Componente renderizado no botão opções,
     * controlado por memo
     */
    Opcoes: (params: GridRenderCellParams) => React.ReactNode,
    /** Mapa `data -> saldoDiaMinutos` da tabela saldo_horas_diario. */
    saldoMap: SaldoDiarioMap,
    /** Edição por digitação do espelho (feature #1522) */
    edicao?: EdicaoEspelhoOptions,
): GridColDefPro[] {
    return [
        {
            field: 'dia',
            headerName: 'Dia',
            width: 60,
            renderCell: ExibirDia,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
        },
        {
            field: 'registros',
            headerName: 'Registros',
            flex: 1,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            headerAlign: 'center',
            renderCell: props => <MemoziedRegistrosCell {...props} />,
        },
        {
            field: 'ausencias',
            headerName: '',
            minWidth: 180,
            flex: 1,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            valueGetter: valueGetterAusencias,
            renderCell: Ausencias,
        },
        {
            field: 'horasTrabalhadas',
            headerName: 'Total',
            width: 80,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            ...(edicao?.habilitada
                ? {
                      renderCell: celulaEditavel(
                          row => edicao.onEditarHora(row, 'horasTrabalhadas'),
                          'Editar horas trabalhadas',
                      ),
                  }
                : { valueFormatter: valueOrNull }),
        },
        ...(exibirSaldoDiario
            ? [
                  {
                      field: 'saldoDiario',
                      headerName: 'Saldo',
                      width: 60,
                      filterable: false,
                      disableColumnMenu: true,
                      sortable: false,
                      align: 'center',
                      headerAlign: 'center',
                      renderCell: (props: GridRenderCellParams) => (
                          <SaldoDiarioCell {...props} saldoMap={saldoMap} />
                      ),
                  },
              ]
            : []),
        {
            field: 'faltas',
            headerName: 'Faltas',
            width: 80,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            valueGetter: ({ value }) => value || '00:00',
            ...(edicao?.habilitada
                ? {
                      renderCell: celulaEditavel(
                          row => edicao.onEditarHora(row, 'faltas'),
                          'Editar faltas',
                      ),
                  }
                : { renderCell: FaltasCell }),
        },
        {
            field: 'horasExtra',
            headerName: 'H. Extras',
            width: 80,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            valueGetter: ({ value }) => value || '00:00',
            ...(edicao?.habilitada
                ? {
                      renderCell: celulaEditavel(
                          edicao.onEditarHorasExtras,
                          'Editar horas extras',
                      ),
                  }
                : {
                      valueFormatter: ({ value }) =>
                          value === '00:00' ? null : value,
                  }),
        },
        {
            field: 'sobreaviso',
            headerName: 'Sobreaviso',
            width: 90,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            valueGetter: ({ value }) => value?.total || '00:00',
            valueFormatter: valueOrNull,
        },
        {
            ...optionsColumn,
            field: 'opcoes',
            renderCell: Opcoes,
        },
    ] as GridColDefPro[]
}

export function diarioColumns(
    Opcoes: (params: GridRenderCellParams) => React.ReactNode,
    /** Edição por digitação do espelho (feature #1522) */
    edicao?: EdicaoEspelhoOptions,
): GridColDefPro[] {
    return [
        {
            field: 'nome',
            headerName: 'Funcionário',
            width: 140,
        },
        {
            field: 'registros',
            headerName: 'Registros',
            minWidth: 770,
            flex: 1,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            headerAlign: 'center',
            renderCell: props => <MemoziedRegistrosCell {...props} />,
        },
        {
            field: 'ausencias',
            headerName: '',
            width: 200,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            valueGetter: valueGetterAusencias,
            renderCell: Ausencias,
        },
        {
            field: 'horasTrabalhadas',
            headerName: 'Total',
            width: 60,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            valueGetter: ({ value }) => value || '00:00',
            ...(edicao?.habilitada
                ? {
                      renderCell: celulaEditavel(
                          row => edicao.onEditarHora(row, 'horasTrabalhadas'),
                          'Editar horas trabalhadas',
                      ),
                  }
                : { valueFormatter: valueOrNull }),
        },
        {
            field: 'faltas',
            headerName: 'Faltas',
            width: 60,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            valueGetter: ({ value }) => value || '00:00',
            ...(edicao?.habilitada
                ? {
                      renderCell: celulaEditavel(
                          row => edicao.onEditarHora(row, 'faltas'),
                          'Editar faltas',
                      ),
                  }
                : { valueFormatter: valueOrNull }),
        },
        {
            field: 'horasExtra',
            headerName: 'H. Extras',
            width: 80,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            valueGetter: ({ value }) => value || '00:00',
            ...(edicao?.habilitada
                ? {
                      renderCell: celulaEditavel(
                          edicao.onEditarHorasExtras,
                          'Editar horas extras',
                      ),
                  }
                : { valueFormatter: valueOrNull }),
        },
        {
            field: 'sobreaviso',
            headerName: 'Sobreaviso',
            width: 90,
            filterable: false,
            disableColumnMenu: true,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            valueGetter: ({ value }) => value?.total || '00:00',
            valueFormatter: valueOrNull,
        },
        {
            ...optionsColumn,
            field: 'opcoes',
            renderCell: Opcoes,
        },
    ]
}

export function useDigitacaoColumns(
    view: ViewMode,
    edicao?: EdicaoEspelhoOptions,
) {
    const hooks = useOpcoesDigitacaoHooks()

    const exibirSaldoDiario = SN.isTrue(hooks.empresa?.exibirSaldoDiario)
    // Só busca o mapa quando faz sentido: view mensal + flag da empresa.
    // Fora disso o hook retorna {} e a célula cai no fallback client-side.
    const saldoMap = useSaldoDiarioMap(view === 'mensal' && exibirSaldoDiario)

    const keys =
        view === 'mensal'
            ? ([
                  hooks.funcionario?.id,
                  hooks.funcionario?.ajustesPonto,
                  hooks.empresa?.id,
                  hooks.empresa?.exibirSaldoDiario,
                  hooks.categoria,
                  view,
                  edicao?.habilitada ? 'e' : '',
                  saldoMap,
              ] as const)
            : ([
                  hooks.empresa?.id,
                  hooks.empresa?.exibirSaldoDiario,
                  view,
                  edicao?.habilitada ? 'e' : '',
              ] as const)

    const columnsMensal = useMemo(
            () =>
                view !== 'mensal'
                    ? []
                    : mensalColumns(
                          SN.isTrue(hooks.empresa?.exibirSaldoDiario),
                          params => <OpcoesDigitacao {...params} {...hooks} />,
                          saldoMap,
                          edicao,
                      ),
            [
                hooks.funcionario?.id,
                hooks.funcionario?.ajustesPonto,
                hooks.empresa?.id,
                hooks.empresa?.exibirSaldoDiario,
                hooks.categoria,
                view,
                edicao?.habilitada,
                saldoMap,
            ],
        ),
        columnsDiario = useMemo(
            () =>
                view !== 'diario'
                    ? []
                    : diarioColumns(
                          params => <OpcoesDigitacao {...params} {...hooks} />,
                          edicao,
                      ),
            [
                hooks.empresa?.id,
                hooks.empresa?.exibirSaldoDiario,
                view,
                edicao?.habilitada,
            ],
        )

    return {
        columns: view === 'mensal' ? columnsMensal : columnsDiario,
        colsHash: keys.join('-'),
    }
}
